import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { verifyCheckoutSession, getUserCredit } from '../lib/creditService';

const PurchaseSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('loading');
  const [details, setDetails] = useState(null);
  const [currentCredits, setCurrentCredits] = useState(0);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (!sessionId) {
      setStatus('error');
      return;
    }

    const verify = async () => {
      try {
        const result = await verifyCheckoutSession(sessionId);
        if (result.success && result.status === 'paid') {
          setDetails(result);
          setStatus('success');
          // Load updated balance
          const balance = await getUserCredit();
          if (balance.success) setCurrentCredits(balance.credit);
        } else {
          setStatus('pending');
        }
      } catch (error) {
        console.error('Error verifying session:', error);
        setStatus('error');
      }
    };

    verify();
  }, [searchParams]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex items-center justify-center">
      <div className="max-w-lg mx-auto px-4">
        {status === 'loading' && (
          <div className="bg-white rounded-2xl shadow-xl p-12 text-center">
            <div className="animate-spin text-4xl mb-4">⏳</div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Verifying Payment...</h2>
            <p className="text-gray-600">Please wait while we confirm your purchase.</p>
          </div>
        )}

        {status === 'success' && (
          <div className="bg-white rounded-2xl shadow-xl p-12 text-center">
            <div className="text-6xl mb-6">🎉</div>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Payment Successful!</h2>
            <div className="bg-green-50 rounded-xl p-6 mb-8">
              <p className="text-lg text-green-800 font-semibold mb-2">
                +{details?.credits || 0} credits added
              </p>
              <p className="text-green-600">Amount paid: ${details?.amount || '0.00'}</p>
              {currentCredits > 0 && (
                <p className="text-green-600 mt-2">Current balance: {currentCredits} credits</p>
              )}
            </div>
            <button
              onClick={() => navigate('/')}
              className="w-full py-3 px-6 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-bold rounded-xl hover:from-blue-700 hover:to-purple-700 transition-all"
            >
              Start Analyzing Apps
            </button>
          </div>
        )}

        {status === 'pending' && (
          <div className="bg-white rounded-2xl shadow-xl p-12 text-center">
            <div className="text-6xl mb-6">⏳</div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Payment Processing</h2>
            <p className="text-gray-600 mb-8">Your payment is being processed. Credits will be added shortly.</p>
            <button onClick={() => navigate('/')} className="text-blue-600 hover:text-blue-800 font-semibold">
              Go to Dashboard
            </button>
          </div>
        )}

        {status === 'error' && (
          <div className="bg-white rounded-2xl shadow-xl p-12 text-center">
            <div className="text-6xl mb-6">❌</div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Something Went Wrong</h2>
            <p className="text-gray-600 mb-8">We couldn't verify your payment. If you were charged, please contact support.</p>
            <div className="space-y-3">
              <button onClick={() => navigate('/purchase')} className="w-full py-3 px-6 bg-blue-600 text-white font-bold rounded-xl hover:bg-blue-700">
                Try Again
              </button>
              <button onClick={() => navigate('/')} className="w-full py-3 px-6 text-gray-600 hover:text-gray-800">
                Go to Dashboard
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PurchaseSuccess;
